// apps/client/src/hooks/useClientSetDetails.ts
import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface LoanDetail {
  loanId: string;
  customerName: string;
  lender: string;
  settlementDate: string;
  loanAmount: number;
  paymentType: string;
  totalCommission: number;
}

export interface ClientSetDetails {
  clientSet: {
    id: string;
    clientSetName: string;
    customerNames: string[];
    createdBy: string;
    createdAt: string;
    loanCount: number;
    totalValueCents: number;
  };
  loans: LoanDetail[];
  summary: {
    totalLoans: number;
    totalLoanBook: number;
    lifetimeCommission: number;
    upfrontCommission: number;
    trailCommission: number;
  };
}

interface UseClientSetDetailsReturn {
  details: ClientSetDetails | null;
  loading: boolean;
  error: string | null;
  fetchDetails: (clientSetId: string) => Promise<void>;
}

/**
 * Hook to fetch detailed information about a client set
 * Calls Cloud Function to get unified portfolio view across all customer names
 */
export function useClientSetDetails(): UseClientSetDetailsReturn {
  const [details, setDetails] = useState<ClientSetDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = async (clientSetId: string) => {
    if (!clientSetId) {
      setError('Client set ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[useClientSetDetails] Fetching details for client set ${clientSetId}`);

      const getClientSetDetailsFunc = httpsCallable<
        { clientSetId: string },
        ClientSetDetails
      >(functions, 'getClientSetDetails');

      const result = await getClientSetDetailsFunc({ clientSetId });

      console.log(`[useClientSetDetails] Received ${result.data.loans.length} loans`);

      setDetails(result.data);
    } catch (err) {
      console.error('[useClientSetDetails] Error fetching client set details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load client set details');
    } finally {
      setLoading(false);
    }
  };

  return {
    details,
    loading,
    error,
    fetchDetails,
  };
}
